import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { unwrapArray } from '../../lib/api-envelope';
import { PermissionNotice, isForbidden } from '../../components/PermissionNotice';
import { workerIdOf } from '../../lib/worker-id';
import { fmtCurrency } from '../../lib/display-labels';
import { ArrowLeft, Plus, Pencil } from 'lucide-react';

export default function WorkerListScreen() {
  const navigate = useNavigate();

  const { data: profilesData, isLoading, error } = useQuery({
    queryKey: ['pay-profiles'],
    queryFn: () => api.payProfiles.list(),
    // A 403 is a settled answer, not a blip. Retrying it three times only delays
    // the message.
    retry: false,
  });

  const forbidden = isForbidden(error);
  const profiles = unwrapArray<any>(profilesData?.data);

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background text-white">
      {/* Header */}
      <div className="border-b border-border px-6 py-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors">
          <ArrowLeft size={24} /> Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground">Workers</h1>
          <div className="flex items-center gap-3">
            {/* Hidden rather than disabled when refused: a greyed-out button still
                says "this is a thing you nearly can do", and creating is exactly
                what this caller may not do. */}
            {!forbidden && (
              <button
                onClick={() => navigate('/payroll/workers/new/pay-profile')}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg text-sm transition"
              >
                <Plus size={16} />
                Add worker
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="max-w-[1200px] mx-auto px-4 py-6">
        {isLoading ? (
          <div className="text-center py-20 text-gray-500">Loading...</div>
        ) : forbidden ? (
          // Was the branch below: "No worker pay profiles yet." plus an invitation
          // to add the first one — a false statement about the company, followed by
          // a button that would itself be refused.
          <PermissionNotice />
        ) : error ? (
          <div className="text-center py-20 text-gray-500">
            Couldn't load worker pay profiles. Try again in a moment.
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 mb-4">No worker pay profiles yet.</p>
            <button
              onClick={() => navigate('/payroll/workers/new/pay-profile')}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg text-sm transition"
            >
              Add first worker
            </button>
          </div>
        ) : (
          <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left px-5 py-3 text-gray-500 font-medium">Name</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-medium">Role</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-medium">Pay basis</th>
                    <th className="text-right px-5 py-3 text-gray-500 font-medium">Base rate</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-medium">Vertical</th>
                    <th className="text-left px-5 py-3 text-gray-500 font-medium">OT rule</th>
                    <th className="text-right px-5 py-3 text-gray-500 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((p: any) => {
                    const name = p.worker_name || p.user_name || p.userName || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown';
                    // `epp.*` carries employee_id, not user_id — see workerIdOf.
                    // Null means we cannot build a working link, and Edit is
                    // hidden rather than pointed at a URL that cannot resolve.
                    const workerId = workerIdOf(p);
                    const payBasisLabel = p.pay_basis === 'day_rate' ? 'Day Rate' : p.pay_basis === 'hourly' ? 'Hourly' : p.pay_basis === 'salary' ? 'Salary' : p.pay_basis || '-';
                    const rateUnit = p.pay_basis === 'day_rate' ? '/day' : p.pay_basis === 'salary' ? '/yr' : '/hr';
                    return (
                      <tr key={p.id || workerId} className="border-b border-white/5 hover:bg-white/5 transition">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs font-bold">
                              {name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <span className="text-white">{name}</span>
                            {!p.active && <span className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded">Inactive</span>}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-gray-300">{p.role_name || '-'}</td>
                        <td className="px-5 py-3 text-gray-300">{payBasisLabel}</td>
                        <td className="px-5 py-3 text-right text-gray-300">
                          {fmtCurrency(p.base_rate, null, 2, rateUnit)}
                        </td>
                        <td className="px-5 py-3 text-gray-300">{p.primary_vertical || '-'}</td>
                        {/*
                          Was `p.ot_rule` — a key `getAllProfiles` has never
                          returned, so this column rendered '-' for every tenant
                          since it shipped. api #412 resolves the rule with the
                          same predicate the payroll engine uses and returns it
                          here, so this cell and the profile screen cannot
                          disagree. Em dash, not '-', per the absent-value rule.
                        */}
                        <td className="px-5 py-3 text-gray-300 text-xs max-w-[180px] truncate">{p.overtime_rule?.label ?? '—'}</td>
                        <td className="px-5 py-3 text-right">
                          {workerId ? (
                            <button
                              onClick={() => navigate(`/payroll/workers/${workerId}/pay-profile`)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-md transition"
                            >
                              <Pencil size={13} />
                              Edit
                            </button>
                          ) : (
                            <span className="text-xs text-gray-500">&mdash;</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-white/5">
              {profiles.map((p: any) => {
                const name = p.worker_name || p.user_name || p.userName || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown';
                const workerId = workerIdOf(p);
                const payBasisLabel = p.pay_basis === 'day_rate' ? 'Day Rate' : p.pay_basis === 'hourly' ? 'Hourly' : p.pay_basis === 'salary' ? 'Salary' : p.pay_basis || '-';
                const rateUnit = p.pay_basis === 'day_rate' ? '/day' : p.pay_basis === 'salary' ? '/yr' : '/hr';
                return (
                  <button
                    key={p.id || workerId}
                    onClick={() => workerId && navigate(`/payroll/workers/${workerId}/pay-profile`)}
                    disabled={!workerId}
                    className="w-full px-5 py-4 text-left hover:bg-white/5 transition disabled:hover:bg-transparent"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-sm font-bold">
                          {name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-medium">{name}</p>
                          <p className="text-xs text-gray-500">{p.role_name || 'No role'} &middot; {payBasisLabel}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white text-sm">
                          {fmtCurrency(p.base_rate, null, 2, rateUnit)}
                        </p>
                        <p className="text-xs text-gray-500">{p.primary_vertical || '-'}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
